/**
 * Shared admin UI primitives + formatters.
 *
 * The admin area is split across multiple sub-pages (/admin/modelos,
 * /admin/configuracion). These bits are reused across them.
 */
import { useEffect, useState } from "react";

const CURRENCY_PREF_KEY = "kc:currency-pref";
const CURRENCY_EVENT = "kc:currency-pref-changed";

/**
 * Per-user currency preference (USD | COP). Stored in localStorage so each
 * admin/super_admin can pick their own view without affecting others. The
 * backend value at `system_config.credits.currency` is the platform default
 * used when no local override exists yet.
 *
 * Returns [currency, setCurrency]. Calling setCurrency persists the value
 * and broadcasts a custom event so other components in the same tab stay
 * in sync (eg. AdminGeneral + AdminModels open in different tabs of the
 * subnav).
 */
export function useCurrencyPref(initialFromBackend) {
    const [currency, setCurrencyState] = useState(() => {
        try {
            return localStorage.getItem(CURRENCY_PREF_KEY) || initialFromBackend || "USD";
        } catch {
            return initialFromBackend || "USD";
        }
    });

    // If the backend default loads after mount and the user hasn't picked
    // a local preference yet, hydrate from backend.
    useEffect(() => {
        if (!initialFromBackend) return;
        try {
            if (!localStorage.getItem(CURRENCY_PREF_KEY)) {
                setCurrencyState(initialFromBackend);
            }
        } catch {
            /* ignore */
        }
    }, [initialFromBackend]);

    // Listen for cross-component changes within the same tab.
    useEffect(() => {
        const handler = (e) => {
            if (e?.detail && (e.detail === "USD" || e.detail === "COP")) {
                setCurrencyState(e.detail);
            }
        };
        window.addEventListener(CURRENCY_EVENT, handler);
        return () => window.removeEventListener(CURRENCY_EVENT, handler);
    }, []);

    const setCurrency = (next) => {
        if (next !== "USD" && next !== "COP") return;
        try {
            localStorage.setItem(CURRENCY_PREF_KEY, next);
        } catch {
            /* ignore */
        }
        setCurrencyState(next);
        window.dispatchEvent(new CustomEvent(CURRENCY_EVENT, { detail: next }));
    };

    return [currency, setCurrency];
}

export const fmtCop = (v) =>
    `$${new Intl.NumberFormat("es-CO", { maximumFractionDigits: 0 }).format(v || 0)}`;

/**
 * Format a plain integer as an es-CO number with dot-thousand separators
 * (1000000 → "1.000.000"). The inverse helper `parseCopDigits` extracts
 * only digits from a formatted string so we can keep controlled inputs
 * displaying the formatted value while storing a pure number.
 */
export const fmtCopDigits = (v) => {
    const n = typeof v === "number" ? v : Number(parseCopDigits(v));
    if (!Number.isFinite(n)) return "";
    return new Intl.NumberFormat("es-CO", { maximumFractionDigits: 0 }).format(n);
};

export const parseCopDigits = (raw) => {
    if (raw === null || raw === undefined) return "";
    return String(raw).replace(/[^\d]/g, "");
};

export const fmtBytes = (bytes) => {
    const gb = bytes / (1024 ** 3);
    if (gb >= 1) return `${gb.toFixed(2)} GB`;
    const mb = bytes / (1024 ** 2);
    if (mb >= 1) return `${mb.toFixed(1)} MB`;
    return `${(bytes / 1024).toFixed(0)} KB`;
};

export function Stat({ label, value }) {
    return (
        <div className="rounded-2xl border hairline border-ink-600 bg-ink-900 p-5">
            <p className="font-mono text-[10px] tracking-widest text-fog-400 uppercase">
                {label}
            </p>
            <p className="font-display text-4xl text-periwinkle-500 mt-2 leading-none">
                {value}
            </p>
        </div>
    );
}

export function CreditStat({ label, value, tone = "default", icon, help }) {
    const toneClass =
        tone === "ok"
            ? "text-periwinkle-500"
            : tone === "warn"
              ? "text-amber-300"
              : tone === "danger"
                ? "text-destructive"
                : "text-fog-100";
    return (
        <div>
            <div className="flex items-center gap-1.5 text-fog-400 mb-1">
                {icon}
                <p className="font-mono text-[10px] tracking-widest uppercase">{label}</p>
                {help}
            </div>
            <p className={`font-display text-3xl ${toneClass}`}>{value}</p>
        </div>
    );
}

export function ColorField({ label, value, onChange, testId }) {
    return (
        <label className="flex items-center gap-3 rounded-xl border hairline border-ink-600 bg-ink-900 px-3 py-2">
            <input
                type="color"
                value={value || "#000000"}
                onChange={(e) => onChange(e.target.value)}
                className="w-9 h-9 rounded-md border hairline border-ink-600 bg-transparent shrink-0 cursor-pointer"
                data-testid={`${testId}-picker`}
            />
            <div className="flex-1 min-w-0">
                <p className="font-mono text-[9px] tracking-widest text-fog-400 uppercase">
                    {label}
                </p>
                <input
                    type="text"
                    value={value || ""}
                    onChange={(e) => onChange(e.target.value)}
                    className="mt-0.5 w-full font-mono text-xs text-fog-100 bg-transparent outline-none"
                    data-testid={`${testId}-hex`}
                />
            </div>
        </label>
    );
}

const FAMILY_STYLES = {
    flux: "border-periwinkle-500/40 text-periwinkle-400",
    "nano-banana": "border-amber-200/30 text-amber-200",
    "gpt-image": "border-emerald-300/30 text-emerald-200",
};

export function FamilyBadge({ family }) {
    if (!family) return null;
    const label =
        family === "nano-banana"
            ? "Nano Banana"
            : family === "gpt-image"
              ? "GPT Image"
              : "FLUX";
    const style = FAMILY_STYLES[family] || FAMILY_STYLES.flux;
    return (
        <span
            className={`font-mono text-[9px] tracking-widest uppercase px-2 py-0.5 rounded-full border hairline ${style}`}
        >
            {label}
        </span>
    );
}
